import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable from "mui-datatables";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { getTeamAssignedFacilities } from "../redux/slices/dashboardSlice";
import DateFilter from "./DateFilter";
import useDateFilter from "./useDateFilter";

const CustomerDashboardData = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Use date filter hook
  const { dateFilter, updateFilter, getDateRange } = useDateFilter();

  // Get team assigned facilities from Redux store
  const {
    teamAssignedFacilities,
    isTeamAssignedFacilitiesLoading,
    teamAssignedFacilitiesError,
  } = useSelector((state) => state.dashboard);

  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // State to trigger table fade-in animation after data loads
  const [tableKey, setTableKey] = useState(0);

  // Fetch team assigned facilities on component mount and when date filters change
  useEffect(() => {
    const dateRange = getDateRange();

    dispatch(
      getTeamAssignedFacilities({
        page: page + 1, // backend usually starts from 1
        per_page: rowsPerPage,
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
      }),
    );
  }, [
    dispatch,
    page,
    rowsPerPage,
    dateFilter.selectedYear,
    dateFilter.viewType,
    dateFilter.selectedQuarter,
    dateFilter.selectedMonth,
  ]);

  // Trigger fade-in animation whenever new facility data arrives (after loading finishes)
  useEffect(() => {
    if (!isTeamAssignedFacilitiesLoading && teamAssignedFacilities?.data) {
      // Bump the key to re-render the table with fade-in class
      setTableKey((prev) => prev + 1);
    }
  }, [isTeamAssignedFacilitiesLoading, teamAssignedFacilities?.data]);

  const handleClick = (rowData) => {
    // Navigate to the correct route with facility ID in the URL
    navigate(`/client-facility/${rowData.id}`, {
      state: {
        facility: rowData,
        facilityId: rowData.id,
      },
    });
  };

  // Transform API data to match table structure
  const transformFacilityData = () => {
    if (
      !teamAssignedFacilities?.data ||
      teamAssignedFacilities.data.length === 0
    ) {
      return [];
    }

    return teamAssignedFacilities.data.map((facility) => ({
      id: facility.id,
      facility: facility.facility_name || "N/A",
      facility_address: facility.facility_address || "N/A",
      hoursWorked: facility.total_hours || "0",
      documents: facility.total_assessments || "0",
      total_score: facility.total_score || "0",
      percentage: facility.percentage + "%" || "0%",
      total_tasks: facility.total_tasks || "0",
      total_assigned_users: facility.total_assigned_users || "0",
      // Include all original data for navigation
      originalData: facility,
    }));
  };

  const employeeColumns = [
    {
      name: "facility",
      label: "Facility",
      options: { filter: false, sort: false },
    },
    {
      name: "facility_address",
      label: "Facility Address",
      options: { filter: false, sort: false },
    },
    {
      name: "hoursWorked",
      label: "Hours Worked",
      options: { filter: false, sort: false },
    },
    // {
    //   name: "documents",
    //   label: "Documents",
    //   options: { filter: false, sort: false },
    // },
    {
      name: "percentage",
      label: "Score",
      options: { filter: false, sort: false },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const rowData = transformFacilityData()[dataIndex];
          return (
            <div style={{ position: "relative" }}>
              <button
                onClick={() => handleClick(rowData.originalData)}
                style={{
                  background: "#8B2885",
                  border: "none",
                  cursor: "pointer",
                  padding: "5px 10px",
                  color: "white",
                  borderRadius: "7px",
                }}
                disabled={isTeamAssignedFacilitiesLoading}>
                <Icon
                  icon="ic:baseline-remove-red-eye"
                  width="17"
                  height="20"
                  style={{ marginRight: "5px" }}
                />
                View Facility
              </button>
            </div>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    rowsPerPageOptions: [10, 25, 50],
    rowsPerPage: rowsPerPage,
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    searchText: searchText,
    onSearchChange: (searchText) => setSearchText(searchText),
    pagination: true,
    serverSide: true,
    page: page,
    count: teamAssignedFacilities?.total || 0,
    onTableChange: (action, tableState) => {
      switch (action) {
        case "changePage":
          setPage(tableState.page);
          break;
        case "changeRowsPerPage":
          setRowsPerPage(tableState.rowsPerPage);
          setPage(0);
          break;
        default:
          break;
      }
    },
    textLabels: {
      body: {
        noMatch: isTeamAssignedFacilitiesLoading
          ? "Loading..."
          : teamAssignedFacilitiesError
            ? "Error loading data"
            : "No matching records found",
      },
    },
  };

  // Show loading state
  if (isTeamAssignedFacilitiesLoading && !teamAssignedFacilities.data?.length) {
    return (
      <div className="mt-40">
        <h2 className="fs-2">Facilities</h2>

        {/* Date Filter Skeleton */}
        <div className="mb-4 pb-2 border-bottom">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div style={{ minWidth: "300px" }}>
              <div className="placeholder-glow">
                <span
                  className="placeholder col-6"
                  style={{ height: "38px" }}></span>
              </div>
            </div>
          </div>
          <div className="mt-2">
            <span className="badge bg-light text-dark p-2 placeholder-glow">
              <span className="placeholder col-4"></span>
            </span>
          </div>
        </div>

        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "200px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (teamAssignedFacilitiesError && !teamAssignedFacilities.data?.length) {
    return (
      <div className="mt-40">
        <h2 className="fs-2">Facilities</h2>

        <div className="mb-4 pb-2 border-bottom">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div style={{ minWidth: "300px" }}>
              <DateFilter
                {...dateFilter}
                onFilterChange={updateFilter}
                size="sm"
              />
            </div>
          </div>
          <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
            <span className="badge bg-light text-dark p-2">
              <i className="fas fa-calendar-alt me-1"></i>
              {getDateRange().label}
            </span>
          </div>
        </div>

        <div className="alert alert-danger" role="alert">
          Error loading facilities: {teamAssignedFacilitiesError}
          <button
            className="btn btn-sm btn-outline-danger ms-3"
            onClick={() => {
              const dateRange = getDateRange();
              dispatch(
                getTeamAssignedFacilities({
                  page: 1,
                  from_date: dateRange.from_date,
                  to_date: dateRange.to_date,
                }),
              );
            }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeSlideIn {
            0% {
              opacity: 0;
              transform: translateY(8px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .fade-table-transition .MuiPaper-root .MuiTableBody-root tr {
            animation: fadeSlideIn 0.25s ease-out forwards;
          }
        `}
      </style>

      <div className="mt-40">
        <h2 className="fs-2">Facilities</h2>

        {/* Date Filter */}
        <div className="mb-4 pb-2 border-bottom">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div style={{ minWidth: "300px" }}>
              <DateFilter
                {...dateFilter}
                onFilterChange={updateFilter}
                size="sm"
              />
            </div>
          </div>

          {/* Active filter badges */}
          <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
            <span className="badge bg-light text-dark p-2">
              <i className="fas fa-calendar-alt me-1"></i>
              {getDateRange().label}
            </span>
          </div>
        </div>

        {teamAssignedFacilities?.data?.length === 0 &&
        !isTeamAssignedFacilitiesLoading ? (
          <div className="alert alert-info" role="alert">
            No facilities found for {getDateRange().label}.
          </div>
        ) : (
          <div
            key={tableKey}
            className="fade-table-transition"
            style={{ transition: "all 0.2s ease" }}>
            <MUIDataTable
              data={transformFacilityData()}
              columns={employeeColumns}
              options={options}
              className="overflow-hidden packageTable"
            />
          </div>
        )}
      </div>
    </>
  );
};

export default CustomerDashboardData;
